import {
  useEffect,
  useState,
} from "react"

import { auth } from "@/api/firebase"
import { BackButton } from "@/components/controls/BackButton"
import {
  CheckoutForm,
  PaymentMethodForm,
  PaymentMethodsList,
} from "@/components/payment"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { useCreatePayment } from "@/hooks/payment"
import { create } from "@bufbuild/protobuf"
import {
  MoneySchema,
  PaymentSchema,
  PaymentStatus,
  TripPathSchema,
  UserPathSchema,
} from "@generated/proto/model"
import { Elements } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"

import type {
  Payment,

  TripPath,
  UserPath,
} from "@generated/proto/model"
// Initialize Stripe
const stripePublishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
const stripePromise = loadStripe(stripePublishableKey)

export function DebugPage() {
  const [selectedTab, setSelectedTab] = useState('payment-methods')
  const [testPayment, setTestPayment] = useState<Payment | null>(null)
  const [userPath, setUserPath] = useState<UserPath | null>(null)
  const [tripPath, setTripPath] = useState<TripPath | null>(null)
  const { createPayment, loading: isCreatingPayment } = useCreatePayment()

  // Get real authenticated user
  useEffect(() => {
    const user = auth.currentUser
    if (user) {
      const realUserPath = create(UserPathSchema, {
        userId: user.uid,
      })
      const realTripPath = create(TripPathSchema, {
        userId: user.uid,
        tripId: 'd2dvq9a9io6s73fhjjb0', // Use real trip ID
      })
      setUserPath(realUserPath)
      setTripPath(realTripPath)
    }
  }, [])

  const handlePaymentMethodSuccess = (result: any) => {
    console.log('Payment method created:', result)
    alert('Payment method added successfully!')
  }

  const handlePaymentSuccess = (result: any) => {
    console.log('Payment completed:', result)
    alert('Payment completed successfully!')
  }

  const createTestPayment = () => {
    if (!tripPath) {
      alert('User not loaded yet, please wait...')
      return
    }

    const payment = create(PaymentSchema, {
      paymentId: '',  // Will be generated by backend
      tripId: tripPath.tripId,
      userId: tripPath.userId,
      amount: create(MoneySchema, { currencyCode: 'USD', units: 99n, nanos: 500000000 }),
      description: 'Hotel booking payment',
      status: PaymentStatus.REQUIRES_CONFIRMATION,
      clientSecret: '',  // Will be generated by backend
      providerIds: [],
      created: undefined,
      modified: undefined,
    })

    createPayment(tripPath.tripId, payment)
      .then((response) => {
        setTestPayment(response.payment || null)
        setSelectedTab('checkout')
      })
      .catch((error) => {
        console.error('Failed to create payment:', error)
        alert('Failed to create test payment')
      })
  }

  // Show loading state while user is being loaded
  if (!userPath || !tripPath) {
    return (
      <div className="container mx-auto px-2 py-2 max-w-4xl">
        <div className="text-center">
          <h1 className="text-lg font-medium text-magazine-ink mb-1 font-sans">
            🦛 Carmen Payment Debug Page
          </h1>
          <p className="text-magazine-charcoal text-sm font-sans">Loading user information...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-2 py-2 max-w-4xl min-h-screen overflow-y-auto">
      <div className="mb-2">
        <div className="flex items-center gap-2 mb-1">
          <BackButton to="/trips" size="sm" />
          <h1 className="text-lg font-medium text-magazine-ink font-sans">
            Payment Debug Page
          </h1>
        </div>
        <p className="text-magazine-charcoal text-sm font-sans">
          Test Stripe payment integration with real components
        </p>
      </div>

      <Elements stripe={stripePromise}>
        <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-2">
          <TabsList className="grid w-full grid-cols-3 h-6">
            <TabsTrigger value="payment-methods" className="text-xs">Payment Methods</TabsTrigger>
            <TabsTrigger value="add-method" className="text-xs">Add Method</TabsTrigger>
            <TabsTrigger value="checkout" className="text-xs">Checkout</TabsTrigger>
          </TabsList>

          <TabsContent value="payment-methods" className="space-y-2">
            <PaymentMethodsList
              userPath={userPath}
              onDelete={(paymentMethodId: string) => {
                console.log('Delete payment method:', paymentMethodId)
                alert(`Deleted payment method: ${paymentMethodId}`)
              }}
            />
          </TabsContent>

          <TabsContent value="add-method" className="space-y-2">
            <Card>
              <CardHeader className="p-2">
                <CardTitle className="text-sm font-medium font-sans">Add Payment Method</CardTitle>
                <CardDescription className="text-xs font-sans">
                  Add a new credit card or payment method using Stripe Elements
                </CardDescription>
              </CardHeader>
              <CardContent className="px-2 pb-2">
                <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-1 text-xs font-sans">Test Cards:</h4>
                  <ul className="text-xs text-blue-800 space-y-0">
                    <li><code>4242 4242 4242 4242</code> - Success</li>
                    <li><code>4000 0000 0000 3220</code> - Requires authentication</li>
                    <li><code>4000 0000 0000 0002</code> - Declined</li>
                  </ul>
                </div>
                <PaymentMethodForm
                  userPath={userPath}
                  onSuccess={handlePaymentMethodSuccess}
                  setAsDefault={true}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="checkout" className="space-y-2">
            <Card>
              <CardHeader className="pt-2">
                <CardTitle className="text-sm font-medium font-sans">Test Checkout Flow</CardTitle>
                <CardDescription className="text-xs font-sans">
                  Create and confirm a test payment ($99.50)
                </CardDescription>
              </CardHeader>
              <CardContent className="p-2">
                <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-1 text-xs font-sans">Test Payment:</h4>
                  <p className="text-xs text-green-800">Amount: $99.50</p>
                  <p className="text-xs text-green-800">Description: Hotel booking payment</p>
                  {testPayment && (
                    <p className="text-xs text-green-800">Payment ID: {testPayment.paymentId}</p>
                  )}
                </div>
                <div className="flex justify-end">
                  {!testPayment ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={createTestPayment}
                      disabled={isCreatingPayment}
                      className="h-4 text-sm"
                    >
                      {isCreatingPayment ? 'Creating Payment...' : 'Create Test Payment'}
                    </Button>
                  ) : (
                    <CheckoutForm
                      payment={testPayment}
                      userPath={userPath}
                      onSuccess={(paymentId) => {
                        handlePaymentSuccess({ paymentId })
                        setTestPayment(null) // Reset for next test
                      }}
                      onCancel={() => setTestPayment(null)}
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </Elements>

      <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-1 text-xs font-sans">Debug Info:</h3>
        <div className="text-xs text-gray-700 space-y-0">
          <p><strong>User ID:</strong> {userPath.userId}</p>
          <p><strong>Trip ID:</strong> {tripPath.tripId}</p>
          <p><strong>Stripe Key:</strong> {stripePublishableKey ? '✅ Loaded' : '❌ Missing'}</p>
          <p><strong>Backend URL:</strong> {import.meta.env.VITE_API_BASE_URL}</p>
        </div>
      </div>
    </div>
  )
}

export default DebugPage